import React from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import BadgeImage from "./BadgeImage";
import EmptyState from "./EmptyState";
import {
  ENTITY_TYPE_PROGRAM,
  findEnrolment,
  formatDate,
  learningJourneyPath,
  sortRewardsNewestFirst,
} from "./rewardsData";

// What the badge was for: a link to the learner's own page for a program
// they are enrolled in, otherwise bold text. A Learning Object page needs
// its program and milestone, which a reward does not carry.
const EntityName = ({ reward, learningPrograms }) => {
  const enrolment =
    reward.entityType === ENTITY_TYPE_PROGRAM &&
    findEnrolment(learningPrograms, reward.entityId);

  if (enrolment) {
    return (
      <Link className="lr-link" to={learningJourneyPath(enrolment.id)}>
        {reward.entityName}
      </Link>
    );
  }
  return <strong>{reward.entityName}</strong>;
};

const BadgeCard = ({ reward, learningPrograms }) => {
  const intl = useIntl();
  const isProgram = reward.entityType === ENTITY_TYPE_PROGRAM;

  return (
    <li className="lr-badge">
      <div className="lr-medal">
        {/* The name is the heading right below: the image is decorative. */}
        <BadgeImage
          artifactUrl={reward.artifactUrl}
          className="lr-medal-image"
          placeholderClassName="lr-medal-placeholder"
        />
      </div>
      <span className="lr-kind">
        {intl.formatMessage({
          id: isProgram ? "my_rewards_kind_program" : "learning_object",
        })}
      </span>
      <h2 className="lr-badge-name">{reward.rewardName}</h2>
      {/* A badge whose program or Learning Object is gone has no name to show. */}
      {reward.entityName && (
        <p className="lr-badge-for">
          {intl.formatMessage(
            { id: "my_rewards_for_completing" },
            {
              name: (
                <EntityName
                  key="name"
                  reward={reward}
                  learningPrograms={learningPrograms}
                />
              ),
            }
          )}
        </p>
      )}
      <p className="lr-badge-when">
        {intl.formatMessage(
          { id: "my_rewards_earned_on" },
          { date: formatDate(intl, reward.awardedAt) }
        )}
      </p>
    </li>
  );
};

const MyRewards = ({ rewards = [], learningPrograms = [], requesting }) => {
  const intl = useIntl();

  if (requesting && rewards.length === 0) {
    return (
      <div className="flex justify-content-center p-4">
        <ProgressSpinner />
      </div>
    );
  }

  if (rewards.length === 0) {
    return (
      <EmptyState
        icon="pi-star"
        message={intl.formatMessage({ id: "no_rewards_earned" })}
        hint={intl.formatMessage({ id: "my_rewards_more_training" })}
      />
    );
  }

  return (
    <>
      {/* Safari drops list semantics from a list without bullets. */}
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
      <ul
        className="lr-badges"
        role="list"
        aria-label={intl.formatMessage({ id: "my_rewards_tab_badges" })}
      >
        {sortRewardsNewestFirst(rewards).map((reward) => (
          <BadgeCard
            key={reward.id}
            reward={reward}
            learningPrograms={learningPrograms}
          />
        ))}
      </ul>
      <p className="lr-note">
        <i className="pi pi-info-circle" aria-hidden="true" />
        <span>{intl.formatMessage({ id: "my_rewards_more_training" })}</span>
      </p>
    </>
  );
};

export default MyRewards;
